import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { getComments, addComment, deleteComment, updateComment, getProfileComments } from '../../../../services/userService';
import { updateLike, addReplyComment, getRepliesByCommentId, deleteReplyComment, updateReplyComment } from '../../../../services/userService';
import { capitalizeFirstOnChange } from '../../../../utils/textFormat';

const DefaultImage = '/images/profile/default-profile.png';

function CourseComment({ contentData, player , mentorId}) {
      const [comments, setComments] = useState([]);
      const [userProfiles, setUserProfiles] = useState({});
      const [showInput, setShowInput] = useState(true);
      const [commentText, setCommentText] = useState('');
      const [editingCommentId, setEditingCommentId] = useState(null);

      const [replyTexts, setReplyTexts] = useState({});
      const [showReplyInputs, setShowReplyInputs] = useState({});
      const [showReplies, setShowReplies] = useState({});
      const [replies, setReplies] = useState({});
      const [editingReplyId, setEditingReplyId] = useState(null);
      const [editReplyTexts, setEditReplyTexts] = useState({});

      const user = JSON.parse(localStorage.getItem('user'));
      const userId = user?.id;

      // console.log('userId--------------local-----------', userId);
      // console.log('mentorId received in CourseComment:', mentorId);

      const videoId = contentData?.id;
      const moduleId = contentData?.module_id;

      useEffect(() => {
        if (videoId) {
          fetchComments();
        }
      }, [videoId]);

      useEffect(() => {
        // Hide replies section when there are no replies left
        Object.keys(replies).forEach(commentId => {
          if (replies[commentId] && replies[commentId].length === 0) {
            setShowReplies(prev => ({
              ...prev,
              [commentId]: false
            }));
          }
        });
      }, [replies]);

      const fetchCommentsRepliesByCommentId = async (commentId) => {
        try {
          const response = await getRepliesByCommentId({ comment_id: commentId });
          // console.log('d-get comment replies by comment id------------------------', response);
          // console.log('Replies for comment', commentId, ':', response);
          if (response.success) {
            setReplies(prev => ({
              ...prev,
              [commentId]: response.data
            }));
          }
      
        }catch (error) {
          console.error('Error fetching replies:', error);
        }
      }

      const fetchUserProfile = async (userId) => {
        try {
          const response = await getProfileComments(userId);
          // console.log('response-------------------------', response);
          if (response.success) {
            setUserProfiles(prev => ({
              ...prev,
              [userId]: response.data
            }));
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      };

      const fetchComments = async () => {
        try {
          // Log mentorId being used in API call
          // console.log('Fetching comments with mentorId:', mentorId);
          
          // Pass both videoId and mentorId to getComments
          const response = await getComments(videoId);
          // console.log('Comments response:', response);
          if (response.success) {
            setComments(response.data);
            // Fetch user profiles for each unique user_id
            const uniqueUserIds = [...new Set(response.data.map(comment => comment.user_id))];
            uniqueUserIds.forEach(userId => {
              fetchUserProfile(userId);
            });
          }
        } catch (error) {
          console.error('Error fetching comments:', error);
        }
      };

      const fetchReplies = async (commentId) => {
        try {
          const response = await getRepliesByCommentId({ comment_id: commentId });
          // console.log('Replies for comment', commentId, ':', response);
          if (response.success) {
            setReplies(prev => ({
              ...prev,
              [commentId]: response.data
            }));
          }
        } catch (error) {
          console.error('Error fetching replies:', error);
        }
      };

      const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
      };

      const handleSaveComment = async () => {
        if (commentText.trim()) {
          try {
            const currentTime = player ? Math.floor(player.getCurrentTime()) : 0;
            const formattedTime = formatTime(currentTime);

            if (editingCommentId) {
              // Handle edit comment
              const response = await updateComment({
                commentId: editingCommentId,
                comment: commentText
              });
              
              if (response.success) {
                // toast.success('Comment updated successfully');
                await fetchComments(); // Refresh comments
              } else {
                toast.error('Failed to update comment');
              }
            } else {
              // Add new comment
              const response = await addComment({
                comment: commentText,
                video_id: videoId,
                module_id: moduleId,
                stream_time: formattedTime,
                mentor_id: mentorId
              });
              
              if (response.success) {
                // toast.success('Comment added successfully');
                await fetchComments();
              } else {
                toast.error('Failed to add comment');
              }
            }
            
            setCommentText('');
            setShowInput(true);
            setEditingCommentId(null);
          } catch (error) {
            console.error('Error saving comment:', error);
            toast.error('Failed to save comment');
          }
        }
      };

      if (!videoId || !moduleId) {
        return <div className="py-6 text-center text-sm text-font-secondary">No content selected</div>;
      }

      const handleDeleteComment = async (commentId) => {
        try {
          const response = await deleteComment(commentId);
          // toast.success('Comment deleted successfully');
          
          if (response.success) {
            // Immediately remove comment from UI
            setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
            // Refresh comments to ensure sync
            await fetchComments();
          }
        } catch (error) {
          console.error('Error deleting comment:', error);
        }
      };

      const handleReplySubmit = async (commentId) => {
        if (replyTexts[commentId]?.trim()) {
          try {
            const response = await addReplyComment({
              parent_comment_id: commentId,
              user_id: userId,
              comment: replyTexts[commentId],
            });
            // console.log('reply commentresponse-------------------------', response);

            if (response.success) {
              // toast.success('Reply added successfully');
              
              // Clear reply text and hide input first
              setReplyTexts(prev => ({ ...prev, [commentId]: '' }));
              setShowReplyInputs(prev => ({ ...prev, [commentId]: false }));
              
              // Re-fetch replies to get the complete updated data with proper IDs
              await fetchCommentsRepliesByCommentId(commentId);
            } else {
              toast.error(response.data?.error_msg || 'Failed to add reply');
            }
          } catch (error) {
            console.error('Error adding reply:', error);
            const errorMsg = error.response?.data?.data?.error_msg || 'Failed to add reply';
            toast.error(errorMsg);
          }
        }
      };

      const handleEditReply = async (replyId, commentId) => {
        if (editReplyTexts[replyId]?.trim()) {
          try {
            const response = await updateReplyComment({
              reply_id: replyId,
              comment: editReplyTexts[replyId]
            });
            
            if (response.success) {
              // toast.success('Reply updated successfully');
              
              // Clear edit state first
              setEditingReplyId(null);
              setEditReplyTexts(prev => ({ ...prev, [replyId]: '' }));
              
              // Re-fetch replies to get the updated data
              await fetchCommentsRepliesByCommentId(commentId);
            } else {
              toast.error('Failed to update reply');
            }
          } catch (error) {
            console.error('Error updating reply:', error);
            const errorMsg = error.response?.data?.error_msg || 'Failed to update reply';
            toast.error(errorMsg);
          }
        }
      };

      const handleDeleteReply = async (replyId, commentId) => {
        try {
          let replyCommentId = replyId;
          const response = await deleteReplyComment(replyCommentId);
          
          if (response.success) {
            // toast.success('Reply deleted successfully');
            
            // Immediately remove the reply from UI for better UX
            setReplies(prev => {
              const updatedReplies = {
                ...prev,
                [commentId]: prev[commentId] ? prev[commentId].filter(reply => reply.id !== replyId) : []
              };
              
              // If no replies left, hide the replies section
              if (updatedReplies[commentId].length === 0) {
                setShowReplies(prevShow => ({
                  ...prevShow,
                  [commentId]: false
                }));
              }
              
              return updatedReplies;
            });
            
            // Also re-fetch replies to ensure data consistency
            await fetchCommentsRepliesByCommentId(commentId);
          } else {
            toast.error('Failed to delete reply');
          }
        } catch (error) {
          console.error('Error deleting reply:', error);
          const errorMsg = error.response?.data?.error_msg || 'Failed to delete reply';
          toast.error(errorMsg);
        }
      };

      return (
        <div>
          {showInput && (
            <div className="mb-6 p-4 rounded-xl border border-gray-200 bg-gray-50/50">
              <textarea
                className="w-full min-h-[100px] px-4 py-3 rounded-lg border border-gray-200 bg-white text-font-primary placeholder:text-font-secondary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y"
                placeholder="Start a discussion..."
                value={commentText}
                onChange={(e) => setCommentText(capitalizeFirstOnChange(e.target.value))}
                autoCapitalize="sentences"
              />
              <div className="flex justify-end gap-2 mt-3">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg font-medium text-font-secondary hover:bg-gray-200 transition"
                  onClick={() => {
                    setCommentText('');
                    setShowInput(true);
                    setEditingCommentId(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg font-medium bg-primary text-white hover:bg-primary/90 transition"
                  onClick={handleSaveComment}
                >
                  {editingCommentId ? 'Update' : 'Post'}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {comments.length > 0 ? (
              comments.map((comment) => (
                <div key={comment.id} className="p-4 rounded-xl border border-gray-200 bg-white">
                  <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <img
                        src={userProfiles[comment.user_id]?.profile_pic || DefaultImage}
                        alt=""
                        className="w-9 h-9 rounded-full object-cover border border-gray-200 shrink-0"
                      />
                      <span className="font-medium text-font-primary truncate">
                        {userProfiles[comment.user_id]?.name || 'Loading...'}
                      </span>
                      {comment.updatedAt && comment.updatedAt !== comment.createdAt && (
                        <span className="text-xs text-gray-400">(edited)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      <span className="text-font-secondary">{new Date(comment.createdAt).toLocaleDateString()}</span>
                      {comment.user_id === userId && (
                        <>
                          <button
                            type="button"
                            className="p-2 rounded-lg text-font-secondary hover:bg-gray-100 hover:text-primary transition"
                            onClick={() => {
                              setCommentText(comment.comment);
                              setEditingCommentId(comment.id);
                              setShowInput(true);
                            }}
                            title="Edit comment"
                          >
                            <Icon icon="mdi:pencil" width="16" height="16" />
                          </button>
                          <button
                            type="button"
                            className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition"
                            onClick={() => handleDeleteComment(comment.id)}
                            title="Delete comment"
                          >
                            <Icon icon="mdi:delete" width="16" height="16" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-font-primary leading-relaxed mb-3">{comment.comment}</p>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                    onClick={() => {
                      const isCurrentlyShown = showReplyInputs[comment.id];
                      setShowReplyInputs((prev) => ({ ...prev, [comment.id]: !isCurrentlyShown }));
                      setShowReplies((prev) => ({ ...prev, [comment.id]: !isCurrentlyShown }));
                      if (!isCurrentlyShown) fetchCommentsRepliesByCommentId(comment.id);
                    }}
                  >
                    <Icon icon="mdi:reply" width="16" height="16" />
                    Reply {replies[comment.id]?.length > 0 && `(${replies[comment.id].length})`}
                  </button>

                  {showReplyInputs[comment.id] && (
                    <div className="mt-3 pl-11 border-l-2 border-gray-100">
                      <p className="text-xs text-font-secondary mb-2">Replying to {userProfiles[comment.user_id]?.name || 'comment'}</p>
                      <textarea
                        className="w-full min-h-[80px] px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 resize-y"
                        placeholder="Write a reply..."
                        value={replyTexts[comment.id] || ''}
                        onChange={(e) => setReplyTexts((prev) => ({ ...prev, [comment.id]: e.target.value }))}
                      />
                      <div className="flex justify-end gap-2 mt-2">
                        <button
                          type="button"
                          className="px-3 py-1.5 rounded-lg text-sm font-medium text-font-secondary hover:bg-gray-100"
                          onClick={() => {
                            setShowReplyInputs((prev) => ({ ...prev, [comment.id]: false }));
                            setReplyTexts((prev) => ({ ...prev, [comment.id]: '' }));
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-primary text-white hover:bg-primary/90"
                          onClick={() => handleReplySubmit(comment.id)}
                        >
                          Reply
                        </button>
                      </div>
                    </div>
                  )}

                  {showReplies[comment.id] && replies[comment.id]?.length > 0 && (
                    <div className="mt-3 pl-6 space-y-3 border-l-2 border-gray-100">
                      {replies[comment.id].map((reply) => (
                        <div key={reply.id} className="py-2">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <img
                                src={userProfiles[reply.user_id]?.profile_pic || DefaultImage}
                                alt=""
                                className="w-6 h-6 rounded-full object-cover shrink-0"
                              />
                              <span className="text-sm font-medium text-font-primary truncate">
                                {reply.name || userProfiles[reply.user_id]?.name || 'Loading...'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              <span className="text-font-secondary">{new Date(reply.createdAt).toLocaleDateString()}</span>
                              {Number(reply.user_id) === Number(userId) && (
                                <>
                                  <button
                                    type="button"
                                    className="p-1.5 rounded text-font-secondary hover:bg-gray-100 hover:text-primary"
                                    onClick={() => {
                                      setEditingReplyId(reply.id);
                                      setEditReplyTexts((prev) => ({ ...prev, [reply.id]: reply.reply_comment || reply.comment }));
                                    }}
                                    title="Edit reply"
                                  >
                                    <Icon icon="mdi:pencil" width="14" height="14" />
                                  </button>
                                  <button
                                    type="button"
                                    className="p-1.5 rounded text-red-500 hover:bg-red-50"
                                    onClick={() => handleDeleteReply(reply.id, comment.id)}
                                    title="Delete reply"
                                  >
                                    <Icon icon="mdi:delete" width="14" height="14" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          {editingReplyId === reply.id ? (
                            <div className="mt-2">
                              <textarea
                                className="w-full min-h-[60px] px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 resize-y"
                                placeholder="Edit your reply..."
                                value={editReplyTexts[reply.id] || ''}
                                onChange={(e) => setEditReplyTexts((prev) => ({ ...prev, [reply.id]: e.target.value }))}
                              />
                              <div className="flex justify-end gap-2 mt-2">
                                <button
                                  type="button"
                                  className="px-3 py-1.5 rounded-lg text-sm text-font-secondary hover:bg-gray-100"
                                  onClick={() => {
                                    setEditingReplyId(null);
                                    setEditReplyTexts((prev) => ({ ...prev, [reply.id]: '' }));
                                  }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-primary text-white"
                                  onClick={() => handleEditReply(reply.id, comment.id)}
                                >
                                  Update
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-font-secondary leading-relaxed">{reply.reply_comment || reply.comment}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-sm text-font-secondary">
                No comments yet.
              </div>
            )}
          </div>
        </div>
      );
    }

    export default CourseComment;